import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import type {
  AccountBook,
  AuditEntry,
  AutocountDefaults,
  CompanyDetail,
  CompanyRole,
  CompanyUser,
  InviteResult,
  License,
  Product,
  Server,
} from '../api/types';
import { COMPANY_ROLE_LABEL } from '../api/types';
import {
  IconArrowLeft,
  IconClock,
  IconKey,
  IconLicense,
  IconPlus,
  IconRefresh,
  IconShield,
  IconUser,
} from '../components/icons';
import { Pill, Runway, StatusPill } from '../components/license';
import { LicenseOptionsSection, type LicenseOptionsHandle } from '../components/productSettings';
import { useCapabilities } from '../lib/auth';
import {
  Button,
  Callout,
  CheckLine,
  ConfirmDialog,
  SelectField,
  Dialog,
  EmptyState,
  ErrorNote,
  SecretReveal,
  SkeletonRows,
  TextAreaField,
  TextField,
  useToast,
} from '../components/ui';
import {
  dateTimeLocalToIso,
  defaultExpiryValue,
  formatDateTime,
  formatRelative,
  humaniseAction,
  licenseConsequence,
  toDateTimeLocal,
} from '../lib/license';

type Tab = 'licenses' | 'users' | 'servers' | 'activity';

export function CompanyDetailPage() {
  const { id = '' } = useParams();
  const [tab, setTab] = useState<Tab>('licenses');

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['company', id],
    queryFn: () => api.get<CompanyDetail>(`/api/staff/companies/${id}`),
    enabled: Boolean(id),
    // Keeps the Credentials "Session" column current while the page is open.
    refetchInterval: 30_000,
  });

  if (isPending) {
    return (
      <section className="panel">
        <SkeletonRows rows={6} />
      </section>
    );
  }

  if (isError || !data) {
    return (
      <>
        <BackLink />
        <ErrorNote error={error} />
      </>
    );
  }

  const { company, licenses, users, servers, accountBooks } = data;

  return (
    <>
      <BackLink />

      <div className="page-head">
        <div>
          <h1>{company.name}</h1>
          <p className="page-head__sub">
            <span className="mono">{company.code}</span>
            {company.contact_person ? ` · ${company.contact_person}` : ''}
            {company.contact_email ? ` · ${company.contact_email}` : ''}
          </p>
        </div>
        <div className="page-head__actions">
          <CompanyActiveToggle company={company} />
        </div>
      </div>

      {(company.synced_from?.length ?? 0) > 0 && (
        <div style={{ marginBottom: 'var(--s5)' }}>
          <Callout tone="info" title={`Synced from ${company.synced_from!.join(' and ')}`}>
            Licenses marked “from” a system take their status and expiry from it, and its logins
            take their passwords from it. Make those changes in that system &mdash; edits here are
            overwritten by the next sync.
          </Callout>
        </div>
      )}

      {!company.is_active && (
        <div style={{ marginBottom: 'var(--s5)' }}>
          <Callout tone="danger" title="This company is deactivated">
            Nobody at {company.name} can sign in to any product, whatever their licenses say.
          </Callout>
        </div>
      )}

      {company.notes && (
        <div style={{ marginBottom: 'var(--s5)' }}>
          <Callout tone="info" title="Notes">
            {company.notes}
          </Callout>
        </div>
      )}

      <div className="tabs" role="tablist" aria-label="Company sections">
        <button
          type="button"
          className="tab"
          role="tab"
          aria-selected={tab === 'licenses'}
          onClick={() => setTab('licenses')}
        >
          Licenses<span className="tab__count">{licenses.length}</span>
        </button>
        <button
          type="button"
          className="tab"
          role="tab"
          aria-selected={tab === 'users'}
          onClick={() => setTab('users')}
        >
          Credentials<span className="tab__count">{users.length}</span>
        </button>
        <button
          type="button"
          className="tab"
          role="tab"
          aria-selected={tab === 'servers'}
          onClick={() => setTab('servers')}
        >
          Servers &amp; account books<span className="tab__count">{accountBooks.length}</span>
        </button>
        <button
          type="button"
          className="tab"
          role="tab"
          aria-selected={tab === 'activity'}
          onClick={() => setTab('activity')}
        >
          Activity
        </button>
      </div>

      {tab === 'licenses' && (
        <LicensesTab companyId={id} licenses={licenses} />
      )}
      {tab === 'users' && (
        <UsersTab companyId={id} users={users} licenses={licenses} accountBooks={accountBooks} />
      )}
      {tab === 'servers' && (
        <ServersTab companyId={id} servers={servers} accountBooks={accountBooks} licenses={licenses} />
      )}
      {tab === 'activity' && <ActivityTab companyId={id} licenses={licenses} />}
    </>
  );
}

function BackLink() {
  return (
    <div style={{ marginBottom: 'var(--s3)' }}>
      {/* link-target rather than a bare inline link: a 16px-tall text link is
          a poor pointer target, and this is the page's primary way back. */}
      <Link to="/companies" className="link-target small" style={{ textDecoration: 'none' }}>
        <IconArrowLeft size={15} /> All companies
      </Link>
    </div>
  );
}

function CompanyActiveToggle({ company }: { company: CompanyDetail['company'] }) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const can = useCapabilities();
  const [confirming, setConfirming] = useState(false);

  const mutation = useMutation({
    mutationFn: (isActive: boolean) =>
      api.patch(`/api/staff/companies/${company.id}`, { isActive }),
    onSuccess: (_data, isActive) => {
      void queryClient.invalidateQueries({ queryKey: ['company', company.id] });
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      push({
        tone: 'success',
        title: isActive ? `${company.name} reactivated` : `${company.name} deactivated`,
      });
      setConfirming(false);
    },
  });

  // Activating/deactivating a company is an edit; read-only support cannot.
  if (!can.canWrite) return null;

  if (!company.is_active) {
    return (
      <Button variant="primary" loading={mutation.isPending} onClick={() => mutation.mutate(true)}>
        Reactivate company
      </Button>
    );
  }

  return (
    <>
      <Button variant="danger" onClick={() => setConfirming(true)}>
        Deactivate company
      </Button>
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => mutation.mutate(false)}
        title="Deactivate this company?"
        // Naming the consequence, not the mechanism — this is what the person
        // on the phone will experience in about fifteen minutes.
        body={`Every user at ${company.name} will be refused sign-in to every product, immediately and regardless of their licenses. Their licenses and credentials are kept, so this is reversible.`}
        confirmLabel="Deactivate"
        confirmWord={company.code}
        loading={mutation.isPending}
        error={mutation.error}
      />
    </>
  );
}

/* ==========================================================================
   Licenses tab
   ========================================================================== */

function LicensesTab({
  companyId,
  licenses,
}: {
  companyId: string;
  licenses: License[];
}) {
  const can = useCapabilities();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<License | null>(null);

  return (
    <>
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">Licenses</h2>
            <p className="panel__hint">
              One live license per product. Renewing means pushing its expiry date forward.
            </p>
          </div>
          {can.canWrite && (
            <Button variant="primary" icon={<IconPlus />} onClick={() => setAdding(true)}>
              Add license
            </Button>
          )}
        </div>

        {licenses.length === 0 ? (
          <EmptyState
            icon={<IconLicense size={30} />}
            title="No licenses recorded"
            body="Record what this company bought and until when. Until a license exists, their credentials cannot sign in to anything."
            action={
              can.canWrite ? (
                <Button variant="primary" icon={<IconPlus />} onClick={() => setAdding(true)}>
                  Add license
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="licenses-list">
            {licenses.map((license) => (
              <li key={license.id}>
                <LicenseRow license={license} canWrite={can.canWrite} onEdit={() => setEditing(license)} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <LicenseDialog
        open={adding}
        companyId={companyId}
        existing={licenses}
        onClose={() => setAdding(false)}
      />

      {editing && (
        <EditLicenseDialog
          license={editing}
          companyId={companyId}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function LicenseRow({
  license,
  canWrite,
  onEdit,
}: {
  license: License;
  canWrite: boolean;
  onEdit: () => void;
}) {
  return (
    <>
      <div className="row row--between" style={{ marginBottom: 'var(--s4)' }}>
        <div className="row" style={{ gap: 'var(--s3)' }}>
          <strong>{license.product_name}</strong>
          <StatusPill license={license} />
          {license.max_users !== null && <Pill>{license.max_users} seats</Pill>}
          {license.synced_from && <Pill tone="neutral">from {license.synced_from}</Pill>}
        </div>
        {canWrite && (
          <Button size="sm" onClick={onEdit}>
            Manage
          </Button>
        )}
      </div>

      {/* The committed element: the period drawn as a period. */}
      <Runway license={license} size="lg" showDate={false} />

      <p
        className={`small${license.is_expired ? ' danger-text' : ' muted'}`}
        style={{ marginTop: 'var(--s3)' }}
      >
        {licenseConsequence(license)}
      </p>

      <dl className="dl" style={{ marginTop: 'var(--s4)' }}>
        <dt>Period</dt>
        <dd className="tabular">
          {formatDateTime(license.starts_at)} → {formatDateTime(license.expires_at)}
        </dd>
        {license.notes ? (
          <>
            <dt>Notes</dt>
            <dd>{license.notes}</dd>
          </>
        ) : null}
      </dl>
    </>
  );
}

function LicenseDialog({
  open,
  companyId,
  existing,
  onClose,
}: {
  open: boolean;
  companyId: string;
  existing: License[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [productId, setProductId] = useState('');
  const [startsAt, setStartsAt] = useState(toDateTimeLocal(new Date()));
  const [expiresAt, setExpiresAt] = useState(defaultExpiryValue(12));
  const [maxUsers, setMaxUsers] = useState('');
  const [notes, setNotes] = useState('');
  const [grantExisting, setGrantExisting] = useState(true);

  const { data: productData } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.get<{ products: Product[] }>('/api/staff/products'),
    enabled: open,
  });

  // A company can only hold one live license per product, so offering a
  // product it already has would guarantee a 409.
  const taken = new Set(existing.filter((l) => l.status !== 'revoked').map((l) => l.product_id));
  const available = (productData?.products ?? []).filter(
    (product) => product.is_active && !taken.has(product.id),
  );

  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ license: License; usersGrantedAccess: number }>('/api/staff/licenses', {
        companyId,
        productId,
        startsAt: dateTimeLocalToIso(startsAt),
        expiresAt: dateTimeLocalToIso(expiresAt),
        maxUsers: maxUsers ? Number(maxUsers) : null,
        notes: notes || undefined,
        grantExistingUsers: grantExisting,
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['licenses'] });
      push({
        tone: 'success',
        title: `${data.license.product_name} licensed`,
        body:
          data.usersGrantedAccess > 0
            ? `${data.usersGrantedAccess} existing credential(s) were granted access.`
            : undefined,
      });
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add license"
      hint="A license is a period of time. Everyone at this company can sign in to the product while it runs, and nobody can once it ends."
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        {mutation.isError && <ErrorNote error={mutation.error} />}

        {available.length === 0 ? (
          <Callout tone="warn" title="No product available">
            This company already holds a live license for every active product. Manage an existing
            license instead, or add a new product first.
          </Callout>
        ) : (
          <>
            <div className="field">
              <label className="field__label" htmlFor="license-product">
                Product
              </label>
              <select
                id="license-product"
                className="select"
                value={productId}
                required
                onChange={(event) => setProductId(event.currentTarget.value)}
              >
                <option value="">Choose a product…</option>
                {available.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-grid form-grid--2">
              <TextField
                label="Starts"
                type="datetime-local"
                value={startsAt}
                required
                onChange={(event) => setStartsAt(event.currentTarget.value)}
              />
              <TextField
                label="Expires"
                type="datetime-local"
                value={expiresAt}
                required
                hint="Access stops at this instant."
                onChange={(event) => setExpiresAt(event.currentTarget.value)}
              />
            </div>

            <div className="row" style={{ gap: 'var(--s2)' }}>
              {[6, 12, 24, 36].map((months) => (
                <Button
                  key={months}
                  size="sm"
                  onClick={() => setExpiresAt(defaultExpiryValue(months))}
                >
                  {months / 12 >= 1 ? `${months / 12} yr` : `${months} mo`}
                </Button>
              ))}
            </div>

            <TextField
              label="Seat limit"
              type="number"
              min={1}
              optional
              value={maxUsers}
              hint="Leave blank for unlimited. Their administrator is refused when creating user N+1."
              onChange={(event) => setMaxUsers(event.currentTarget.value)}
            />

            <TextAreaField
              label="Notes"
              optional
              value={notes}
              onChange={(event) => setNotes(event.currentTarget.value)}
            />

            <CheckLine
              title="Grant this product to existing credentials"
              description="Their people can already sign in to other products; tick this so they can use this one too."
              checked={grantExisting}
              onChange={setGrantExisting}
            />
          </>
        )}

        <div className="form-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="primary"
            disabled={available.length === 0 || !productId}
            loading={mutation.isPending}
          >
            Add license
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function EditLicenseDialog({
  license,
  companyId,
  onClose,
}: {
  license: License;
  companyId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const can = useCapabilities();
  const [expiresAt, setExpiresAt] = useState(toDateTimeLocal(new Date(license.expires_at)));
  const [maxUsers, setMaxUsers] = useState(license.max_users != null ? String(license.max_users) : '');
  const [notes, setNotes] = useState(license.notes ?? '');
  const [revoking, setRevoking] = useState(false);
  // The options section saves through this handle, so one button saves both.
  const optionsRef = useRef<LicenseOptionsHandle>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['company', companyId] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['licenses'] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const result = await api.patch<{ license: License }>(`/api/staff/licenses/${license.id}`, {
        expiresAt: dateTimeLocalToIso(expiresAt),
        maxUsers: maxUsers ? Number(maxUsers) : null,
        notes: notes || null,
      });
      // ...and the per-company option values, in the same click.
      await optionsRef.current?.save();
      return result;
    },
    onSuccess: (data) => {
      invalidate();
      push({
        tone: 'success',
        title: data.license.is_expired ? 'Saved — license still expired' : 'Saved',
        body: licenseConsequence(data.license),
      });
      onClose();
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: 'active' | 'suspended' | 'revoked') =>
      api.patch<{ license: License }>(`/api/staff/licenses/${license.id}`, { status }),
    onSuccess: (data, status) => {
      invalidate();
      push({
        tone: status === 'active' ? 'success' : 'info',
        title:
          status === 'active'
            ? 'License reinstated'
            : status === 'suspended'
              ? 'License suspended'
              : 'License revoked',
        body: licenseConsequence(data.license),
      });
      setRevoking(false);
      onClose();
    },
  });

  return (
    <>
      <Dialog open onClose={onClose} title={`${license.product_name} license`} wide>
        <div className="stack">
          {(save.isError || setStatus.isError) && (
            <ErrorNote error={save.error ?? setStatus.error} />
          )}

          <div>
            <Runway license={license} size="lg" showDate={false} />
            <p className={`small${license.is_expired ? ' danger-text' : ' muted'}`} style={{ marginTop: 'var(--s3)' }}>
              {licenseConsequence(license)}
            </p>
          </div>

          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            <TextField
              label="Expires"
              type="datetime-local"
              value={expiresAt}
              required
              hint="Push this forward to renew. Everyone regains access immediately."
              onChange={(event) => setExpiresAt(event.currentTarget.value)}
            />

            <div className="row" style={{ gap: 'var(--s2)' }}>
              <span className="small faint">Extend from today:</span>
              {[3, 6, 12, 24].map((months) => (
                <Button
                  key={months}
                  size="sm"
                  onClick={() => setExpiresAt(defaultExpiryValue(months))}
                >
                  +{months >= 12 ? `${months / 12} yr` : `${months} mo`}
                </Button>
              ))}
            </div>

            <TextField
              label="Seat limit"
              type="number"
              min={1}
              optional
              value={maxUsers}
              hint="Leave blank for unlimited. Their administrator is refused when creating user N+1."
              onChange={(event) => setMaxUsers(event.currentTarget.value)}
            />

            <TextAreaField
              label="Notes"
              optional
              value={notes}
              onChange={(event) => setNotes(event.currentTarget.value)}
            />
          </form>

          <LicenseOptionsSection
            ref={optionsRef}
            licenseId={license.id}
          />

          {/* One button saves the licence fields above and the per-company
              options together. */}
          <div className="form-actions">
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </div>

          <div className="subsection">
            <div className="stack stack--tight">
              <strong className="subsection__title">Switch access off</strong>
              <p className="small muted">
                Suspending blocks sign-in but keeps the dates, so it is reversible in one click.
                Revoking retires the license for good and frees the product to be licensed again.
              </p>
              <div className="row" style={{ gap: 'var(--s2)', marginTop: 'var(--s2)' }}>
                {license.status === 'active' ? (
                  <Button
                    variant="secondary"
                    loading={setStatus.isPending && setStatus.variables === 'suspended'}
                    onClick={() => setStatus.mutate('suspended')}
                  >
                    Suspend
                  </Button>
                ) : license.status === 'suspended' ? (
                  <Button
                    variant="primary"
                    loading={setStatus.isPending && setStatus.variables === 'active'}
                    onClick={() => setStatus.mutate('active')}
                  >
                    Reinstate
                  </Button>
                ) : (
                  <Pill tone="suspended">Revoked</Pill>
                )}

                {/* Revoking is a destroy — admin only; deployment may suspend
                    but not revoke. */}
                {license.status !== 'revoked' && can.canDestroy && (
                  <Button variant="danger" onClick={() => setRevoking(true)}>
                    Revoke
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        open={revoking}
        onClose={() => setRevoking(false)}
        onConfirm={() => setStatus.mutate('revoked')}
        title="Revoke this license?"
        body={`Everyone at ${license.company_name} loses access to ${license.product_name} immediately. The record is kept, but the license cannot be reinstated — you would add a new one.`}
        confirmLabel="Revoke license"
        confirmWord={license.product_code}
        loading={setStatus.isPending}
        error={setStatus.error}
      />
    </>
  );
}

/* ==========================================================================
   Credentials tab
   ========================================================================== */

function UsersTab({
  companyId,
  users,
  licenses,
  accountBooks,
}: {
  companyId: string;
  users: CompanyUser[];
  licenses: License[];
  accountBooks: AccountBook[];
}) {
  const can = useCapabilities();
  const [inviting, setInviting] = useState(false);
  const [managing, setManaging] = useState<CompanyUser | null>(null);
  const hasLicense = licenses.some((license) => license.status !== 'revoked');

  const superadmins = users.filter((user) => user.company_role === 'superadmin');

  // Products this company is licensed for, or that any login can use.
  const productOptions = [
    ...new Map(
      [
        ...licenses
          .filter((license) => license.status !== 'revoked')
          .map((license) => [license.product_id, license.product_name] as const),
        ...users.flatMap((user) => user.products.map((p) => [p.id, p.name] as const)),
      ],
    ).entries(),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  const [productFilter, setProductFilter] = useState('');
  const shownUsers = productFilter
    ? users.filter((user) => user.products.some((p) => p.id === productFilter))
    : users;

  return (
    <>
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">Credentials</h2>
            <p className="panel__hint">
              You issue the administrator credential. That administrator then creates their own
              people from inside the product — those appear here too.
            </p>
          </div>
          <div className="row" style={{ gap: 'var(--s2)', alignItems: 'center', flexWrap: 'wrap' }}>
            {productOptions.length > 0 && (
              <select
                className="select"
                aria-label="Filter credentials by product"
                value={productFilter}
                onChange={(e) => setProductFilter(e.currentTarget.value)}
              >
                <option value="">All products ({users.length})</option>
                {productOptions.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name} ({users.filter((u) => u.products.some((p) => p.id === id)).length})
                  </option>
                ))}
              </select>
            )}
            {can.canWrite && (
              <Button variant="primary" icon={<IconPlus />} onClick={() => setInviting(true)}>
                New credential
              </Button>
            )}
          </div>
        </div>

        {!hasLicense && users.length === 0 && (
          <div className="panel__body">
            <Callout tone="warn" title="Add a license first">
              A credential created before any license exists cannot sign in anywhere, because access
              is granted per licensed product.
            </Callout>
          </div>
        )}

        {users.length === 0 ? (
          <EmptyState
            icon={<IconUser size={30} />}
            title="No credentials yet"
            body="Create the administrator credential for this company. They receive an email, choose their own password, and can then create their own users inside the product."
            action={
              can.canWrite ? (
                <Button variant="primary" icon={<IconPlus />} onClick={() => setInviting(true)}>
                  New credential
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="table-wrap scroll-area">
            <table className="table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Products</th>
                  <th>Account book</th>
                  <th>Last sign-in</th>
                  <th>Session</th>
                  <th className="table__actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shownUsers.length === 0 && (
                  <tr>
                    <td colSpan={9} className="cell-meta">
                      No credentials can use this product.
                    </td>
                  </tr>
                )}
                {shownUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="cell-stack">
                        <span className="cell-strong mono">{user.username}</span>
                        {user.email && <span className="cell-meta">{user.email}</span>}
                        {(user.synced_from?.length ?? 0) > 0 && (
                          <span className="cell-meta">from {user.synced_from!.join(', ')}</span>
                        )}
                      </div>
                    </td>
                    <td>{user.full_name ?? <span className="cell-meta">—</span>}</td>
                    <td>
                      <div className="cell-stack">
                        <span>
                          {user.company_role === 'superadmin' ? (
                            <Pill tone="pending">Super admin</Pill>
                          ) : user.company_role === 'admin' ? (
                            <Pill tone="live">Admin</Pill>
                          ) : (
                            <span className="cell-meta">User</span>
                          )}
                        </span>
                        {user.created_by_username && (
                          <span className="cell-meta">by {user.created_by_username}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <UserStatus user={user} />
                    </td>
                    <td className="cell-meta">
                      {user.products.length === 0
                        ? 'none'
                        : user.products.map((product) => product.name).join(', ')}
                    </td>
                    <td className="cell-meta">
                      {(user.account_books ?? []).length === 0 ? (
                        '—'
                      ) : (
                        <div className="cell-stack">
                          {user.account_books!.map((b) => (
                            <span key={b.book_id}>
                              {b.book_name}
                              <span className="faint"> · {b.product_name ?? 'any product'}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="cell-meta tabular">{formatRelative(user.last_login_at)}</td>
                    <td>
                      <SessionCell companyId={companyId} user={user} canRelease={can.canWrite} />
                    </td>
                    <td className="table__actions">
                      {can.canWrite && (
                        <Button size="sm" onClick={() => setManaging(user)}>
                          Manage
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <InviteUserDialog
        open={inviting}
        companyId={companyId}
        licenses={licenses}
        accountBooks={accountBooks}
        onClose={() => setInviting(false)}
      />

      {managing && (
        <ManageUserDialog
          user={managing}
          companyId={companyId}
          accountBooks={accountBooks}
          isLastAdmin={superadmins.length === 1 && managing.company_role === 'superadmin'}
          onClose={() => setManaging(null)}
        />
      )}
    </>
  );
}

function UserStatus({ user }: { user: CompanyUser }) {
  const locked = user.locked_until !== null && new Date(user.locked_until) > new Date();

  if (!user.is_active) return <Pill tone="expired">Deactivated</Pill>;
  if (locked) return <Pill tone="expired">Locked</Pill>;
  if (!user.has_password) return <Pill tone="expiring">Invite pending</Pill>;
  if (user.must_change_password) return <Pill tone="pending">Must change password</Pill>;
  return <Pill tone="live">Active</Pill>;
}

/**
 * The one account book a login uses, grouped by server ("KL server › KL
 * Trading"). The empty value means no book. Users never see this — only staff.
 */
/** Book choices keyed by product id ('' = a book on a server for any product). */
type BookChoice = Record<string, string>;

/** The company's books grouped by the product they are for, in the order staff see them. */
function bookGroups(accountBooks: AccountBook[]) {
  const groups = new Map<string, { key: string; label: string; books: AccountBook[] }>();
  for (const book of accountBooks) {
    const key = book.product_id ?? '';
    const group = groups.get(key) ?? { key, label: book.product_name ?? 'Any product', books: [] };
    group.books.push(book);
    groups.set(key, group);
  }
  // Named products first, "Any product" last.
  return [...groups.values()].sort(
    (x, y) => Number(x.key === '') - Number(y.key === '') || x.label.localeCompare(y.label),
  );
}

/** What a login holds now, as a BookChoice. */
const choiceOf = (user: CompanyUser): BookChoice =>
  Object.fromEntries((user.account_books ?? []).map((b) => [b.product_id ?? '', b.book_id]));

/** One account-book dropdown per product that has books: the database the login opens there. */
function AccountBooksPicker({
  accountBooks,
  value,
  onChange,
}: {
  accountBooks: AccountBook[];
  value: BookChoice;
  onChange: (next: BookChoice) => void;
}) {
  const groups = bookGroups(accountBooks);
  if (groups.length === 0) {
    return (
      <p className="small muted">
        No account books yet, so this login opens no AutoCount database. Add them on the
        &ldquo;Servers &amp; account books&rdquo; tab when a product needs one.
      </p>
    );
  }
  return (
    <>
      {groups.map((group) => (
        <SelectField
          key={group.key}
          label={`Account book — ${group.label}`}
          value={value[group.key] ?? ''}
          hint="The AutoCount database this login opens in that product. The user cannot change it themselves."
          onChange={(event) => onChange({ ...value, [group.key]: event.currentTarget.value })}
        >
          <option value="">No account book</option>
          {group.books.map((book) => (
            <option key={book.id} value={book.id}>
              {book.server_name} › {book.name}
            </option>
          ))}
        </SelectField>
      ))}
    </>
  );
}

/**
 * Whether this login is signed in right now, and a way to sign it out. For a
 * one-device product (the 360 Dashboard) releasing frees the account, so the
 * next sign-in goes straight through instead of asking to take over.
 */
function SessionCell({
  companyId,
  user,
  canRelease,
}: {
  companyId: string;
  user: CompanyUser;
  canRelease: boolean;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [confirming, setConfirming] = useState(false);
  const sessions = user.active_sessions ?? [];

  const release = useMutation({
    mutationFn: () => api.post<{ released: number }>(`/api/staff/users/${user.id}/release-sessions`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      push({
        tone: 'success',
        title: `${user.username} released`,
        body: 'The next sign-in goes straight through; the device using it now is signed out.',
      });
      setConfirming(false);
    },
  });

  if (sessions.length === 0) return <span className="cell-meta">—</span>;

  const latest = sessions[0]!;
  return (
    <div className="cell-stack">
      <span>
        <Pill tone="live">Signed in</Pill>
      </span>
      <span className="cell-meta" title={latest.ip ? `From ${latest.ip}` : undefined}>
        {sessions.map((s) => s.product_name).join(', ')} · active {formatRelative(latest.last_active_at)}
      </span>
      {canRelease && (
        <span>
          <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
            Release
          </Button>
        </span>
      )}
      <ConfirmDialog
        open={confirming}
        title={`Release ${user.username}?`}
        body="This signs the login out of every product. Whoever is using it now is signed out at their next page load or within 15 minutes, and the next sign-in is let straight through."
        confirmLabel="Release"
        loading={release.isPending}
        error={release.error}
        onConfirm={() => release.mutate()}
        onClose={() => {
          release.reset();
          setConfirming(false);
        }}
      />
    </div>
  );
}

function InviteUserDialog({
  open,
  companyId,
  licenses,
  accountBooks,
  onClose,
}: {
  open: boolean;
  companyId: string;
  licenses: License[];
  accountBooks: AccountBook[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [companyRole, setCompanyRole] = useState<CompanyRole>('superadmin');
  const [books, setBooks] = useState<BookChoice>({});
  const [result, setResult] = useState<InviteResult | null>(null);

  const productNames = licenses
    .filter((license) => license.status !== 'revoked')
    .map((license) => license.product_name);

  const mutation = useMutation({
    mutationFn: () =>
      api.post<InviteResult>(`/api/staff/companies/${companyId}/users`, {
        username,
        email,
        fullName: fullName || undefined,
        companyRole,
        accountBookIds: Object.values(books).filter(Boolean),
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      setResult(data);
      push({
        tone: data.invite.emailStatus === 'sent' ? 'success' : 'error',
        title:
          data.invite.emailStatus === 'sent'
            ? `Invite sent to ${data.user.email}`
            : 'Credential created, but the email failed',
        body:
          data.invite.emailStatus === 'sent'
            ? undefined
            : 'Use “Resend invite” once mail is working, or set a password directly.',
      });
    },
  });

  const close = () => {
    setUsername('');
    setEmail('');
    setFullName('');
    setCompanyRole('superadmin');
    setBooks({});
    setResult(null);
    mutation.reset();
    onClose();
  };

  // After creation the dialog becomes a receipt: the invite link is only
  // recoverable here (in console-driver mode) and from the sent-email log.
  if (result) {
    return (
      <Dialog open={open} onClose={close} title="Credential created">
        <div className="stack">
          <dl className="dl">
            <dt>Username</dt>
            <dd className="mono">{result.user.username}</dd>
            <dt>Invite sent to</dt>
            <dd>{result.user.email}</dd>
            <dt>Products</dt>
            <dd>{result.products.length > 0 ? result.products.join(', ') : 'none yet'}</dd>
          </dl>

          {result.warnings.map((warning) => (
            <Callout key={warning} tone="warn">
              {warning}
            </Callout>
          ))}

          {result.invite.devLink ? (
            <>
              <Callout tone="info" title="No mail is being sent">
                Email is running in console mode, so nothing left the server. Send this link to the
                customer yourself, or configure SMTP.
              </Callout>
              <SecretReveal label="Set-password link" value={result.invite.devLink} />
            </>
          ) : (
            <Callout tone="info">
              They have been emailed a link to choose their own password. Nobody here can see it —
              if they never received it, use “Resend invite”, which invalidates the previous link.
            </Callout>
          )}

          <div className="form-actions">
            <Button variant="primary" onClick={close}>
              Done
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="New credential"
      hint="You never choose their password. They receive an email and set it themselves."
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        {mutation.isError && <ErrorNote error={mutation.error} />}

        <TextField
          label="Username"
          value={username}
          required
          mono
          autoFocus
          hint="Unique across every company, so prefix it with the company code — acme.admin."
          onChange={(event) => setUsername(event.currentTarget.value)}
        />

        <TextField
          label="Email"
          type="email"
          value={email}
          required
          hint="The invite goes here. It must be a mailbox they can actually read."
          onChange={(event) => setEmail(event.currentTarget.value)}
        />

        <TextField
          label="Full name"
          optional
          value={fullName}
          onChange={(event) => setFullName(event.currentTarget.value)}
        />

        <SelectField
          label="Rank"
          value={companyRole}
          hint={
            companyRole === 'superadmin'
              ? 'The credential you hand over when a company subscribes. It can create administrators, and nothing they do can modify it back.'
              : companyRole === 'admin'
                ? 'Can manage ordinary users only. Cannot see or touch a super admin.'
                : 'An ordinary user. Cannot manage anyone.'
          }
          onChange={(event) => setCompanyRole(event.currentTarget.value as CompanyRole)}
        >
          <option value="superadmin">Super admin — hand this to the customer</option>
          <option value="admin">Admin — manages users only</option>
          <option value="member">User — manages nobody</option>
        </SelectField>

        <AccountBooksPicker accountBooks={accountBooks} value={books} onChange={setBooks} />

        <Callout tone="info" title="They will be able to sign in to">
          {productNames.length > 0
            ? productNames.join(', ')
            : 'nothing yet — this company has no license, so add one first.'}
        </Callout>

        <div className="form-actions">
          <Button onClick={close}>Cancel</Button>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            Create and send invite
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function ManageUserDialog({
  user,
  companyId,
  accountBooks,
  isLastAdmin,
  onClose,
}: {
  user: CompanyUser;
  companyId: string;
  accountBooks: AccountBook[];
  isLastAdmin: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [password, setPassword] = useState('');
  const [books, setBooks] = useState<BookChoice>(() => choiceOf(user));
  const heldBooks = choiceOf(user);
  const changedBooks = bookGroups(accountBooks)
    .map((g) => g.key)
    .filter((key) => (books[key] ?? '') !== (heldBooks[key] ?? ''));
  const [resend, setResend] = useState<{ devLink?: string } | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['company', companyId] });
    void queryClient.invalidateQueries({ queryKey: ['companies'] });
  };

  const locked = user.locked_until !== null && new Date(user.locked_until) > new Date();

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/api/staff/users/${user.id}`, body),
    onSuccess: (_data, body) => {
      invalidate();
      const label =
        'isActive' in body
          ? body.isActive
            ? 'Credential reactivated'
            : 'Credential deactivated'
          : 'unlock' in body
            ? 'Lockout cleared'
            : 'Credential updated';
      push({ tone: 'success', title: label });
      if ('isActive' in body) onClose();
    },
  });

  const resendInvite = useMutation({
    mutationFn: () =>
      api.post<{ emailStatus: string; devLink?: string }>(
        `/api/staff/users/${user.id}/resend-invite`,
      ),
    onSuccess: (data) => {
      invalidate();
      setResend({ devLink: data.devLink });
      push({
        tone: data.emailStatus === 'sent' ? 'success' : 'error',
        title:
          data.emailStatus === 'sent'
            ? 'New invite sent — the previous link no longer works'
            : 'The email failed to send',
      });
    },
  });

  const setPasswordMutation = useMutation({
    mutationFn: () =>
      api.post(`/api/staff/users/${user.id}/set-password`, {
        password,
        mustChangePassword: true,
      }),
    onSuccess: () => {
      invalidate();
      setPassword('');
      push({
        tone: 'success',
        title: 'Password set',
        body: 'They will be asked to change it when they next sign in.',
      });
    },
  });

  return (
    <Dialog open onClose={onClose} title={user.username} wide>
      <div className="stack">
        <dl className="dl">
          <dt>Status</dt>
          <dd>
            <UserStatus user={user} />
          </dd>
          <dt>Rank</dt>
          <dd>{COMPANY_ROLE_LABEL[user.company_role]}</dd>
          <dt>Email</dt>
          <dd>{user.email ?? <span className="faint">none</span>}</dd>
          <dt>Products</dt>
          <dd>
            {user.products.length > 0
              ? user.products.map((product) => product.name).join(', ')
              : 'none'}
          </dd>
          <dt>Created</dt>
          <dd className="tabular">
            {formatDateTime(user.created_at)}
            {user.created_by_username ? ` by ${user.created_by_username}` : ''}
          </dd>
          <dt>Last sign-in</dt>
          <dd className="tabular">{formatDateTime(user.last_login_at)}</dd>
        </dl>

        {(patch.isError || resendInvite.isError || setPasswordMutation.isError) && (
          <ErrorNote
            error={patch.error ?? resendInvite.error ?? setPasswordMutation.error}
          />
        )}

        {locked && (
          <Callout tone="warn" title="Locked out by failed sign-ins">
            Automatically clears at {formatDateTime(user.locked_until)}, or clear it now.
            <div style={{ marginTop: 'var(--s3)' }}>
              <Button
                size="sm"
                loading={patch.isPending}
                onClick={() => patch.mutate({ unlock: true })}
              >
                Clear lockout
              </Button>
            </div>
          </Callout>
        )}

        {resend?.devLink && (
          <>
            <Callout tone="info" title="No mail is being sent">
              Email is in console mode. Pass this link to the customer yourself.
            </Callout>
            <SecretReveal label="Set-password link" value={resend.devLink} />
          </>
        )}

        <div className="subsection">
          <div className="stack stack--tight">
            <strong className="subsection__title">Password</strong>
            <p className="small muted">
              Sending a link is the better option — it means nobody here ever knows their password.
              Setting one directly is for customers who cannot receive email.
            </p>
            <div className="row" style={{ gap: 'var(--s2)', marginTop: 'var(--s2)' }}>
              <Button
                icon={<IconRefresh />}
                loading={resendInvite.isPending}
                disabled={!user.email}
                title={user.email ? undefined : 'This user has no email address'}
                onClick={() => resendInvite.mutate()}
              >
                {user.has_password ? 'Send reset link' : 'Resend invite'}
              </Button>
            </div>

            <form
              className="row"
              style={{ gap: 'var(--s2)', marginTop: 'var(--s3)', alignItems: 'flex-end' }}
              onSubmit={(event) => {
                event.preventDefault();
                setPasswordMutation.mutate();
              }}
            >
              <div className="grow">
                <TextField
                  label="Or set a password directly"
                  type="text"
                  value={password}
                  autoComplete="off"
                  placeholder="At least 10 characters"
                  onChange={(event) => setPassword(event.currentTarget.value)}
                />
              </div>
              <Button
                type="submit"
                icon={<IconKey />}
                disabled={password.length === 0}
                loading={setPasswordMutation.isPending}
              >
                Set
              </Button>
            </form>
          </div>
        </div>

        <div className="subsection">
          <div className="stack stack--tight">
            <strong className="subsection__title">Account books</strong>
            <p className="small muted">
              The AutoCount database this login opens in each product. Add servers and books on the
              &ldquo;Servers &amp; account books&rdquo; tab.
            </p>
            <form
              className="stack stack--tight"
              style={{ marginTop: 'var(--s2)' }}
              onSubmit={(event) => {
                event.preventDefault();
                patch.mutate({
                  accountBooks: changedBooks.map((key) => ({
                    productId: key || null,
                    accountBookId: books[key] || null,
                  })),
                });
              }}
            >
              <AccountBooksPicker accountBooks={accountBooks} value={books} onChange={setBooks} />
              {accountBooks.length > 0 && (
                <div>
                  <Button type="submit" loading={patch.isPending} disabled={changedBooks.length === 0}>
                    Save books
                  </Button>
                </div>
              )}
            </form>
          </div>
        </div>

        <div className="subsection">
          <div className="stack stack--tight">
            <strong className="subsection__title">Access</strong>
            <div className="row" style={{ gap: 'var(--s2)', marginTop: 'var(--s2)' }}>
            {/* Staff can move a credential to any rank; the strictly-below rule
                constrains the customer's own admins, not us. */}
            {(['superadmin', 'admin', 'member'] as const)
              .filter((role) => role !== user.company_role)
              .map((role) => (
                <Button
                  key={role}
                  icon={<IconShield />}
                  loading={patch.isPending}
                  disabled={isLastAdmin && role !== 'superadmin'}
                  title={
                    isLastAdmin && role !== 'superadmin'
                      ? 'This is the company’s only super admin. Promote someone else first.'
                      : undefined
                  }
                  onClick={() => patch.mutate({ companyRole: role })}
                >
                  Make {COMPANY_ROLE_LABEL[role].toLowerCase()}
                </Button>
              ))}

              {user.is_active ? (
                <Button
                  variant="danger"
                  loading={patch.isPending}
                  disabled={isLastAdmin}
                  title={
                    isLastAdmin
                      ? 'This is the company’s only administrator. Promote someone else first.'
                      : undefined
                  }
                  onClick={() => patch.mutate({ isActive: false })}
                >
                  Deactivate
                </Button>
              ) : (
                <Button
                  variant="primary"
                  loading={patch.isPending}
                  onClick={() => patch.mutate({ isActive: true })}
                >
                  Reactivate
                </Button>
              )}
            </div>
            {isLastAdmin && (
              <p className="small faint" style={{ marginTop: 'var(--s2)' }}>
                <IconClock size={13} /> Deactivating the last super admin would leave this company
                unable to manage its own users.
              </p>
            )}
          </div>
        </div>

        <div className="form-actions">
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/* ==========================================================================
   Activity tab
   ========================================================================== */

/* ==========================================================================
   Servers & account books tab
   ========================================================================== */

function ServersTab({
  companyId,
  servers,
  accountBooks,
  licenses,
}: {
  companyId: string;
  servers: Server[];
  accountBooks: AccountBook[];
  licenses: License[];
}) {
  const can = useCapabilities();
  // A server is for one of the products this company is licensed for.
  const products = [
    ...new Map(
      licenses
        .filter((l) => l.status !== 'revoked')
        .map((l) => [l.product_id, { id: l.product_id, name: l.product_name }] as const),
    ).values(),
  ];
  const [serverDialog, setServerDialog] = useState<Server | 'new' | null>(null);
  const [bookDialog, setBookDialog] = useState<AccountBook | 'new' | null>(null);
  // Clicking a server narrows the books to the ones on it; null shows them all.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = servers.find((s) => s.id === selectedId) ?? null;
  const shownBooks = selected ? accountBooks.filter((b) => b.server_id === selected.id) : accountBooks;

  return (
    <>
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">Servers</h2>
            <p className="panel__hint">
              The AutoCount SQL servers this company runs, each for one product. Only products that
              read AutoCount need one.
            </p>
          </div>
          {can.canWrite && (
            <Button variant="primary" icon={<IconPlus />} onClick={() => setServerDialog('new')}>
              Add server
            </Button>
          )}
        </div>

        {servers.length === 0 ? (
          <EmptyState
            title="No servers yet"
            body="Add the company’s AutoCount SQL server, then add the account books that live on it."
            action={
              can.canWrite ? (
                <Button variant="primary" icon={<IconPlus />} onClick={() => setServerDialog('new')}>
                  Add server
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="table-wrap scroll-area">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Product</th>
                  <th>Address</th>
                  <th>SQL user</th>
                  <th>SQL password</th>
                  <th>Books</th>
                  <th className="table__actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {servers.map((server) => (
                  <ServerRow
                    key={server.id}
                    server={server}
                    selected={server.id === selectedId}
                    onSelect={() => setSelectedId(server.id === selectedId ? null : server.id)}
                    canWrite={can.canWrite}
                    canDestroy={can.canDestroy}
                    onEdit={() => setServerDialog(server)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">
              {selected ? `Account books on ${selected.name}` : 'Account books'}
            </h2>
            <p className="panel__hint">
              {selected
                ? 'Showing only this server’s books. Click the server again, or Show all, to see every book.'
                : 'Each book is one AutoCount database on one server. Click a server above to see only its books. Assign a login a book on the Credentials tab.'}
            </p>
          </div>
          <span className="row" style={{ gap: 'var(--s2)' }}>
            {selected && <Button onClick={() => setSelectedId(null)}>Show all</Button>}
            {can.canWrite && servers.length > 0 && (
              <Button variant="primary" icon={<IconPlus />} onClick={() => setBookDialog('new')}>
                Add account book
              </Button>
            )}
          </span>
        </div>

        {shownBooks.length === 0 ? (
          <EmptyState
            title={selected ? `No account books on ${selected.name} yet` : 'No account books yet'}
            body={
              selected
                ? 'Add the AutoCount databases that sit on this server.'
                : servers.length === 0
                ? 'Add a server first, then its account books.'
                : 'Add the AutoCount databases that sit on this company’s servers.'
            }
          />
        ) : (
          <div className="table-wrap scroll-area">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Database</th>
                  <th>Product</th>
                  <th>Server</th>
                  <th>Logins</th>
                  <th className="table__actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shownBooks.map((book) => (
                  <BookRow
                    key={book.id}
                    book={book}
                    canWrite={can.canWrite}
                    canDestroy={can.canDestroy}
                    onEdit={() => setBookDialog(book)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {serverDialog && (
        <ServerDialog
          companyId={companyId}
          products={products}
          server={serverDialog === 'new' ? null : serverDialog}
          onClose={() => setServerDialog(null)}
        />
      )}
      {bookDialog && (
        <BookDialog
          companyId={companyId}
          servers={servers}
          defaultServerId={selected?.id}
          book={bookDialog === 'new' ? null : bookDialog}
          onClose={() => setBookDialog(null)}
        />
      )}
    </>
  );
}

function ServerRow({
  server,
  selected,
  onSelect,
  canWrite,
  canDestroy,
  onEdit,
}: {
  server: Server;
  selected: boolean;
  onSelect: () => void;
  canWrite: boolean;
  canDestroy: boolean;
  onEdit: () => void;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [deleting, setDeleting] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.del(`/api/staff/servers/${server.id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['company', server.company_id] });
      setDeleting(false);
      push({ tone: 'success', title: `${server.name} removed` });
    },
  });

  return (
    <tr
      data-clickable="true"
      aria-selected={selected}
      title={selected ? 'Show every account book' : 'Show only this server’s account books'}
      onClick={onSelect}
    >
      <td className="cell-strong">{server.name}</td>
      <td>{server.product_name ?? <span className="cell-meta">Any product</span>}</td>
      <td className="cell-mono">
        {server.address}:{server.sql_port}
      </td>
      <td className="cell-mono">{server.sql_user ?? <span className="cell-meta">—</span>}</td>
      <td>
        {server.sql_password_set ? (
          <Pill tone="live">Stored</Pill>
        ) : (
          <span className="cell-meta">Not set</span>
        )}
      </td>
      <td className="table__num">{server.book_count}</td>
      {/* Its buttons and delete dialog are not a click on the row. */}
      <td className="table__actions" onClick={(e) => e.stopPropagation()}>
        <span className="row" style={{ gap: 'var(--s2)', justifyContent: 'flex-end' }}>
          {canWrite && (
            <Button size="sm" onClick={onEdit}>
              Edit
            </Button>
          )}
          {canDestroy && (
            <Button
              size="sm"
              variant="danger"
              disabled={server.book_count > 0}
              title={
                server.book_count > 0
                  ? 'Remove its account books first.'
                  : 'Delete this server.'
              }
              onClick={() => setDeleting(true)}
            >
              Delete
            </Button>
          )}
        </span>
        <ConfirmDialog
          open={deleting}
          onClose={() => {
            remove.reset();
            setDeleting(false);
          }}
          onConfirm={() => remove.mutate()}
          title={`Delete ${server.name}?`}
          body="This removes the server. It is only possible once it has no account books."
          confirmLabel="Delete server"
          loading={remove.isPending}
          error={remove.error}
        />
      </td>
    </tr>
  );
}

function BookRow({
  book,
  canWrite,
  canDestroy,
  onEdit,
}: {
  book: AccountBook;
  canWrite: boolean;
  canDestroy: boolean;
  onEdit: () => void;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [deleting, setDeleting] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.del(`/api/staff/account-books/${book.id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['company', book.company_id] });
      setDeleting(false);
      push({ tone: 'success', title: `${book.name} removed` });
    },
  });

  return (
    <tr>
      <td className="cell-strong">{book.name}</td>
      <td className="cell-mono">{book.database_name}</td>
      <td>{book.product_name ?? <span className="cell-meta">Any product</span>}</td>
      <td>{book.server_name}</td>
      <td className="table__num">{book.login_count}</td>
      <td className="table__actions">
        <span className="row" style={{ gap: 'var(--s2)', justifyContent: 'flex-end' }}>
          {canWrite && (
            <Button size="sm" onClick={onEdit}>
              Edit
            </Button>
          )}
          {canDestroy && (
            <Button size="sm" variant="danger" onClick={() => setDeleting(true)}>
              Delete
            </Button>
          )}
        </span>
        <ConfirmDialog
          open={deleting}
          onClose={() => {
            remove.reset();
            setDeleting(false);
          }}
          onConfirm={() => remove.mutate()}
          title={`Delete ${book.name}?`}
          body={
            book.login_count > 0
              ? `This un-assigns the ${book.login_count} login(s) that currently use it — they keep working but open no book until reassigned.`
              : 'This removes the account book.'
          }
          confirmLabel="Delete account book"
          loading={remove.isPending}
          error={remove.error}
        />
      </td>
    </tr>
  );
}

function ServerDialog({
  companyId,
  products,
  server,
  onClose,
}: {
  companyId: string;
  products: Array<{ id: string; name: string }>;
  server: Server | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const editing = server !== null;

  const [name, setName] = useState(server?.name ?? '');
  // '' = any product: only offered for a server that already had none.
  const [productId, setProductId] = useState(
    server ? (server.product_id ?? '') : products.length === 1 ? products[0]!.id : '',
  );
  const [address, setAddress] = useState(server?.address ?? '');
  const [port, setPort] = useState(String(server?.sql_port ?? 1433));
  const [sqlUser, setSqlUser] = useState(server?.sql_user ?? '');
  const [notes, setNotes] = useState(server?.notes ?? '');
  // Password is write-only. keep = leave the stored value, set = replace with a
  // new value, remove = clear it.
  const [pwMode, setPwMode] = useState<'keep' | 'set' | 'remove'>(
    editing ? 'keep' : 'set',
  );
  const [pwValue, setPwValue] = useState('');

  const { data: defaults } = useQuery({
    queryKey: ['autocount-defaults'],
    queryFn: () => api.get<AutocountDefaults>('/api/staff/autocount/defaults'),
    staleTime: Infinity,
  });
  // A new server starts with the usual AutoCount login, editable.
  useEffect(() => {
    if (!editing && defaults) setSqlUser((current) => current || defaults.sqlUser);
  }, [editing, defaults]);

  // Connects with what is on screen; a blank password falls back to the
  // stored one (editing) and then to Heron's default.
  const test = useMutation({
    mutationFn: () =>
      api.post<{ databases: string[] }>('/api/staff/autocount/databases', {
        serverId: editing ? server!.id : undefined,
        address: address.trim(),
        sqlPort: Number(port),
        sqlUser: sqlUser.trim() || undefined,
        sqlPassword: pwMode === 'set' && pwValue ? pwValue : undefined,
      }),
  });

  const save = useMutation({
    mutationFn: () => {
      const base = {
        productId: productId || null,
        name,
        address,
        sqlPort: Number(port),
        sqlUser: sqlUser.trim() || null,
        notes: notes.trim() || null,
      };
      const password =
        pwMode === 'set'
          ? { sqlPassword: pwValue }
          : pwMode === 'remove'
            ? { sqlPassword: null }
            : {};
      return editing
        ? api.patch(`/api/staff/servers/${server!.id}`, { ...base, ...password })
        : api.post(`/api/staff/companies/${companyId}/servers`, {
            ...base,
            sqlUser: sqlUser.trim() || undefined,
            notes: notes.trim() || undefined,
            ...(pwValue ? { sqlPassword: pwValue } : {}),
          });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      push({ tone: 'success', title: editing ? `${name} saved` : `${name} added` });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={() => {
        save.reset();
        onClose();
      }}
      title={editing ? `Edit ${server!.name}` : 'Add server'}
      hint="An AutoCount SQL server: where to reach it and the SQL login the product uses."
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <ErrorNote error={save.error} />}

        <TextField label="Name" value={name} required autoFocus onChange={(e) => setName(e.currentTarget.value)} />
        <SelectField
          label="Product"
          value={productId}
          required={!editing || server!.product_id !== null}
          hint={
            products.length === 0
              ? 'This company has no licence yet. Add one on the Licenses tab first.'
              : 'Its account books are for this product only. Logins pick a book per product.'
          }
          onChange={(e) => setProductId(e.currentTarget.value)}
        >
          {editing && server!.product_id === null ? (
            <option value="">Any product</option>
          ) : (
            <option value="" disabled>
              Choose a product…
            </option>
          )}
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </SelectField>
        <div className="form-grid form-grid--2">
          <TextField
            label="Address"
            value={address}
            required
            mono
            placeholder="autocountx.ddns.net"
            hint="Host name or IP, reachable from the internet (Heron runs in the cloud)."
            onChange={(e) => setAddress(e.currentTarget.value)}
          />
          <TextField
            label="SQL port"
            type="number"
            value={port}
            required
            onChange={(e) => setPort(e.currentTarget.value)}
          />
        </div>
        <TextField
          label="SQL user"
          optional
          value={sqlUser}
          mono
          placeholder="sa"
          onChange={(e) => setSqlUser(e.currentTarget.value)}
        />

        {/* Write-only SQL password, like a secret product option. */}
        <div className="subsection">
          <div className="stack stack--tight">
            <strong className="subsection__title">SQL password</strong>
            {editing && (
              <p className="small muted">
                {server!.sql_password_set ? 'A password is stored.' : 'No password is stored.'} It is
                never shown. Replace it or remove it below.
              </p>
            )}
            {editing ? (
              <>
                <div className="row" style={{ gap: 'var(--s2)', flexWrap: 'wrap' }}>
                  <label className="small row" style={{ gap: 'var(--s1)' }}>
                    <input type="radio" checked={pwMode === 'keep'} onChange={() => setPwMode('keep')} />
                    Keep
                  </label>
                  <label className="small row" style={{ gap: 'var(--s1)' }}>
                    <input type="radio" checked={pwMode === 'set'} onChange={() => setPwMode('set')} />
                    Replace
                  </label>
                  <label className="small row" style={{ gap: 'var(--s1)' }}>
                    <input
                      type="radio"
                      checked={pwMode === 'remove'}
                      onChange={() => setPwMode('remove')}
                      disabled={!server!.sql_password_set}
                    />
                    Remove
                  </label>
                </div>
                {pwMode === 'set' && (
                  <TextField
                    label="New SQL password"
                    value={pwValue}
                    required
                    autoComplete="off"
                    onChange={(e) => setPwValue(e.currentTarget.value)}
                  />
                )}
              </>
            ) : (
              <TextField
                label="SQL password"
                optional
                value={pwValue}
                autoComplete="off"
                hint={
                  defaults?.defaultPasswordSet
                    ? 'Leave blank to use the default AutoCount password. Stored encrypted; the console never shows it again.'
                    : 'Stored encrypted; the console never shows it again.'
                }
                onChange={(e) => setPwValue(e.currentTarget.value)}
              />
            )}
          </div>
        </div>

        <div className="stack stack--tight">
          <div className="row" style={{ gap: 'var(--s2)' }}>
            <Button
              type="button"
              loading={test.isPending}
              disabled={!address.trim() || !port}
              onClick={() => test.mutate()}
            >
              Test connection
            </Button>
          </div>
          {test.isError && <ErrorNote error={test.error} />}
          {test.isSuccess && (
            <Callout tone="info" title="Connected">
              {test.data.databases.length === 0
                ? 'The login works, but it can see no databases.'
                : `${test.data.databases.length} database${test.data.databases.length === 1 ? '' : 's'} found: ${test.data.databases.join(', ')}`}
            </Callout>
          )}
        </div>

        <TextAreaField
          label="Notes"
          optional
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />

        <div className="form-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {editing ? 'Save server' : 'Add server'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function BookDialog({
  companyId,
  servers,
  defaultServerId,
  book,
  onClose,
}: {
  companyId: string;
  servers: Server[];
  /** The server a new book starts on, e.g. the one being viewed. */
  defaultServerId?: string;
  book: AccountBook | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const editing = book !== null;

  const [name, setName] = useState(book?.name ?? '');
  const [databaseName, setDatabaseName] = useState(book?.database_name ?? '');
  const [serverId, setServerId] = useState(book?.server_id ?? defaultServerId ?? servers[0]?.id ?? '');
  const [autocountCompanyId, setAutocountCompanyId] = useState(book?.autocount_company_id ?? '');
  // Type the name instead of picking it, for a server Heron cannot reach.
  const [manual, setManual] = useState(false);

  // Asks the chosen server which databases it has, so the name is picked
  // rather than typed. Connection details come from the saved server.
  const detected = useQuery({
    queryKey: ['autocount-databases', serverId],
    queryFn: () => api.post<{ databases: string[] }>('/api/staff/autocount/databases', { serverId }),
    enabled: Boolean(serverId) && !manual,
    retry: false,
    staleTime: 60_000,
  });
  const databases = detected.data?.databases ?? [];
  const pickable = !manual && detected.isSuccess;

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        databaseName,
        serverId,
        autocountCompanyId: autocountCompanyId.trim() || (editing ? null : undefined),
      };
      return editing
        ? api.patch(`/api/staff/account-books/${book!.id}`, body)
        : api.post(`/api/staff/companies/${companyId}/account-books`, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      push({ tone: 'success', title: editing ? `${name} saved` : `${name} added` });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={() => {
        save.reset();
        onClose();
      }}
      title={editing ? `Edit ${book!.name}` : 'Add account book'}
      hint="One AutoCount database on one of this company’s servers."
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <ErrorNote error={save.error} />}

        <TextField label="Name" value={name} required autoFocus onChange={(e) => setName(e.currentTarget.value)} />
        <SelectField
          label="Server"
          value={serverId}
          required
          onChange={(e) => setServerId(e.currentTarget.value)}
        >
          {servers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} ({s.address}:{s.sql_port}) · {s.product_name ?? 'any product'}
            </option>
          ))}
        </SelectField>

        {pickable ? (
          <SelectField
            label="Database"
            value={databaseName}
            required
            hint={`${databases.length} found on this server.`}
            onChange={(e) => setDatabaseName(e.currentTarget.value)}
          >
            <option value="">Choose a database…</option>
            {/* Keep a saved name visible even if the server no longer lists it. */}
            {databaseName && !databases.includes(databaseName) && (
              <option value={databaseName}>{databaseName} (not found on server)</option>
            )}
            {databases.map((db) => (
              <option key={db} value={db}>
                {db}
              </option>
            ))}
          </SelectField>
        ) : (
          <TextField
            label="Database"
            value={databaseName}
            required
            mono
            hint={
              detected.isFetching
                ? 'Looking for databases on this server…'
                : 'The AutoCount database name.'
            }
            onChange={(e) => setDatabaseName(e.currentTarget.value)}
          />
        )}
        {!manual && detected.isError && <ErrorNote error={detected.error} />}
        <div className="row" style={{ gap: 'var(--s2)' }}>
          {!manual && (
            <Button
              type="button"
              size="sm"
              icon={<IconRefresh size={14} />}
              loading={detected.isFetching}
              disabled={!serverId}
              onClick={() => void detected.refetch()}
            >
              Detect again
            </Button>
          )}
          <Button type="button" size="sm" onClick={() => setManual((m) => !m)}>
            {manual ? 'Pick from the server instead' : 'Type the name instead'}
          </Button>
        </div>
        <TextField
          label="AutoCount company id"
          optional
          value={autocountCompanyId}
          mono
          onChange={(e) => setAutocountCompanyId(e.currentTarget.value)}
        />

        <div className="form-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {editing ? 'Save book' : 'Add account book'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function ActivityTab({ companyId, licenses }: { companyId: string; licenses: License[] }) {
  // Empty = everything; a product id = only entries about that product.
  const [productId, setProductId] = useState('');
  const products = [...new Map(licenses.map((l) => [l.product_id, l.product_name] as const)).entries()];

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['audit', companyId, productId],
    queryFn: () =>
      api.get<{ entries: AuditEntry[] }>(
        `/api/staff/audit-logs?companyId=${companyId}&limit=100${productId ? `&productId=${productId}` : ''}`,
      ),
  });

  if (isPending) {
    return (
      <section className="panel">
        <SkeletonRows />
      </section>
    );
  }

  if (isError) {
    return <ErrorNote error={error} />;
  }

  const entries = data?.entries ?? [];

  return (
    <section className="panel">
      <div className="panel__head">
        <div>
          <h2 className="panel__title">Activity</h2>
          <p className="panel__hint">
            Everything that happened to this company — including failed sign-ins.
            {productId && ' Filtered to one product: entries not tied to a product (invites, company changes) are hidden.'}
          </p>
        </div>
        {products.length > 0 && (
          <select
            className="select"
            aria-label="Filter activity by product"
            value={productId}
            onChange={(e) => setProductId(e.currentTarget.value)}
          >
            <option value="">All activity</option>
            {products.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        )}
      </div>

      {entries.length === 0 ? (
        <EmptyState
          icon={<IconClock size={30} />}
          title="Nothing recorded yet"
          body="Licenses, credentials and sign-ins are all logged here as they happen."
        />
      ) : (
        <div className="table-wrap scroll-area">
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>What</th>
                <th>Who</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="cell-meta tabular nowrap">{formatDateTime(entry.created_at)}</td>
                  <td className="cell-strong">{humaniseAction(entry.action)}</td>
                  <td className="cell-meta">{entry.actor_label ?? entry.actor_type}</td>
                  <td className="cell-meta">
                    <MetadataSummary metadata={entry.metadata} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function MetadataSummary({ metadata }: { metadata: Record<string, unknown> }) {
  const entries = Object.entries(metadata ?? {});
  if (entries.length === 0) return <span className="faint">—</span>;

  return (
    <span>
      {entries
        .map(([key, value]) => `${key}: ${formatMetaValue(value)}`)
        .join(' · ')}
    </span>
  );
}

function formatMetaValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
